import { useEffect, useState } from 'react';
import { AppLayout } from '../../components/layout';
import {
  EmptyState,
  MetricCard,
  StatusBadge,
  formatDuration,
} from '../../components/core';
import { http } from '../../lib/api';
import type { TenantDashboardDto } from '@livestreamx/shared-types';
import { useUiStore } from '../../store/ui';
import { NAV } from './nav';

export function DashboardPage() {
  const ui = useUiStore();
  const [data, setData] = useState<TenantDashboardDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void http
      .get<TenantDashboardDto>('/api/tenant/dashboard')
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load dashboard'));
  }, []);

  if (error) {
    return (
      <AppLayout brand="LiveStreamX" navGroups={NAV}>
        <h1 className="page-title">Dashboard</h1>
        <EmptyState title="Unable to load the dashboard" body={error} />
      </AppLayout>
    );
  }
  if (!data) {
    return (
      <AppLayout brand="LiveStreamX" navGroups={NAV}>
        <h1 className="page-title">Dashboard</h1>
        <div className="mt-4 text-muted">Loading your streams…</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout brand="LiveStreamX" navGroups={NAV}>
      <h1 className="page-title">Dashboard</h1>
      <p className="page-subtitle">
        {data.subscriptionStatus
          ? `${data.currentPlanName} plan · subscription ${data.subscriptionStatus}`
          : 'No active plan — subscribe on the Billing page to create streams'}
        {data.subscriptionExpiresAt
          ? ` · renews ${new Date(data.subscriptionExpiresAt).toLocaleDateString()}`
          : ''}
      </p>

      <div className="grid-stats">
        <MetricCard label="Live streams" value={data.liveStreams} hint="Currently broadcasting" />
        <MetricCard label="Total streams" value={data.totalStreams} />
        <MetricCard label="Active destinations" value={data.activeDestinations} hint={`${data.totalDestinations} total`} />
        <MetricCard label="Bandwidth" value={`${data.bandwidthUsedGb} GB`} />
      </div>

      <div className="mt-4">
        <h2 className="mb-2">Live now</h2>
        {data.liveNow.length === 0 ? (
          <EmptyState
            title="No live streams"
            body="Create a stream, connect OBS, and start broadcasting to see it here in real time."
          />
        ) : (
          <div className="grid-3">
            {data.liveNow.map((s) => (
              <div key={s.id} className="stream-card">
                <div className="flex items-center gap-2">
                  <h3><a href={`/streams/${s.slug || s.id}`}>{s.name}</a></h3>
                  <StatusBadge status="LIVE" />
                </div>
                <div className="text-muted text-sm mt-2">
                  {s.startedAt
                    ? <>Started {formatDuration(
                      Math.floor((Date.now() - new Date(s.startedAt).getTime()) / 1000),
                    )} ago</>
                    : ''}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}